import React, { useState, useEffect } from 'react';
import { Plus, X, Users, ArrowLeft } from 'lucide-react';
import AutocompleteInput from './AutocompleteInput';
import { PREDEFINED_NAMES } from '../data/database';

const AttendeeManager = ({ attendees: initialAttendees = [], onAttendeesAdded, onBack }) => {
  const [attendees, setAttendees] = useState(initialAttendees.length > 0 ? initialAttendees : ['']);
  const [error, setError] = useState('');

  // Update local state when initialAttendees prop changes
  useEffect(() => {
    if (initialAttendees.length > 0) {
      setAttendees(initialAttendees);
    }
  }, [initialAttendees]);

  const addAttendee = () => {
    setAttendees([...attendees, '']);
  };

  const removeAttendee = (index) => {
    if (attendees.length > 1) {
      const newAttendees = attendees.filter((_, i) => i !== index);
      setAttendees(newAttendees);
    }
  };

  const updateAttendee = (index, value) => {
    const newAttendees = [...attendees];
    newAttendees[index] = value;
    setAttendees(newAttendees);
  };

  const handleContinue = () => {
    const validAttendees = attendees.filter(name => name.trim() !== '');
    
    if (validAttendees.length === 0) {
      setError('Please add at least one attendee');
      return;
    }

    if (validAttendees.length !== new Set(validAttendees).size) {
      setError('All attendee names must be unique');
      return;
    }

    setError('');
    onAttendeesAdded(validAttendees);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Add Attendees</h2>
        <p className="text-gray-600">Enter the names of everyone who will be splitting the bill</p>
      </div>

      <div className="card max-w-2xl mx-auto">
        <div className="space-y-4">
          {attendees.map((attendee, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-sm">
                      {index + 1}
                    </span>
                  </div>
                </div>
                
                <div className="flex-1">
                  <AutocompleteInput
                    value={attendee}
                    onChange={(value) => updateAttendee(index, value)}
                    options={PREDEFINED_NAMES}
                    placeholder={`Attendee ${index + 1} name`}
                    className="w-full"
                    allowCustom={true}
                  />
                </div>
                
                {attendees.length > 1 && (
                  <button
                    onClick={() => removeAttendee(index)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    title="Remove attendee"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            onClick={addAttendee}
            className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add another person</span>
          </button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to scanning</span>
          </button>

          <button
            onClick={handleContinue}
            className="btn-primary flex items-center space-x-2"
          >
            <Users className="h-4 w-4" />
            <span>Continue to allocation</span>
          </button>
        </div>
      </div>

      <div className="text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
          <h3 className="font-medium text-yellow-900 mb-2">Pro tip:</h3>
          <p className="text-sm text-yellow-700">
            You can always add or remove people later. Make sure names are unique so you can easily identify who owes what!
          </p>
        </div>
      </div>
    </div>
  );
};

export default AttendeeManager;
